import React, { useEffect, useState } from "react";

import { NavLink, useLocation, useNavigate } from "react-router-dom";

import MyCouponLogo from "../../../../assets/images/MyCouponLogo.svg";
import MyCouponLogoMobile from "../../../../assets/images/MyCouponLogoMobile.svg";
import ImpersonationSwitcher from "containers/Dashboard/Components/ImpersonationSwitcher";

// ─── Section map: which sections exist on each page ───────────────────────────
// NOTE: no longer used for header nav (replaced by NAV_LINKS below), kept
// exported in case other code still imports PAGE_SECTIONS.
// NOTE: keys below are still "/" and "/business" — this file's routes moved to
// "/landing" and "/landing/business", so SectionDotNav will currently render
// nothing on either page. Flagging again since it wasn't part of this change.
export const PAGE_SECTIONS: Record<string, { id: string; label: string }[]> = {
  "/": [
    { id: "hero", label: "Home" },
    { id: "smart-deals", label: "Smart Deals" },
    { id: "about", label: "About" },
    { id: "categories", label: "Categories" },
    { id: "personalised", label: "Personalised" },
    { id: "save-redeem", label: "Save & Redeem" },
    { id: "rewards", label: "Rewards" },
    { id: "premium", label: "Premium" },
    { id: "download", label: "Download" },
  ],
  "/business": [
    { id: "hero", label: "Hero" },
    { id: "stats", label: "Stats" },
    { id: "profile", label: "Profile" },
    { id: "why-coupons", label: "Why Coupons" },
    { id: "brands", label: "Brands" },
    { id: "deal-ideas", label: "Deal Ideas" },
    { id: "advantage", label: "Advantage" },
    { id: "community", label: "Community" },
    { id: "faq", label: "FAQ" },
    { id: "founding-partner", label: "Founding Partner" },
    { id: "free-demo", label: "Free Demo" },
  ],
  "/page3": [
    { id: "pricing-hero", label: "Overview" },
    { id: "pricing-plans", label: "Plans" },
  ],
};

/* ── Nav icons — copied from CustomerHeader.js so the landing header's pill
   nav matches the app header's icon set exactly. Each accepts `active`: true
   renders filled (solid), false renders outline (stroke only). ── */

const HomeIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.15 1.86a1.35 1.35 0 0 1 1.7 0l6.53 5.28c.4.33.62.81.62 1.32v8.04c0 .94-.76 1.7-1.7 1.7h-4.15a.85.85 0 0 1-.85-.85v-4.5c0-.4-.05-.75-.13-.93a1.55 1.55 0 0 0-1.17-.94 1.9 1.9 0 0 0-.7 0 1.55 1.55 0 0 0-1.17.94c-.08.18-.13.53-.13.93v4.5c0 .47-.38.85-.85.85H3.3c-.94 0-1.7-.76-1.7-1.7V8.46c0-.51.22-.99.62-1.32l6.93-5.28Z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.4}
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon = ({ active }: { active: boolean }) => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle
      cx="8.5"
      cy="8.5"
      r="6.2"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.6}
    />
    <path d="M13.4 13.4 18 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BusinessesIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.5 8.2 3.6 2.6h12.8l1.1 5.6"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 8v8.6c0 .5.4.9.9.9h12.2c.5 0 .9-.4.9-.9V8"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 17.5V12h4v5.5"
      fill={active ? "#fff" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
  </svg>
);

const MapsIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 18.2s6.3-5.6 6.3-10.4a6.3 6.3 0 1 0-12.6 0c0 4.8 6.3 10.4 6.3 10.4Z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
    <circle
      cx="10"
      cy="7.8"
      r="2.3"
      fill={active ? "#fff" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
    />
  </svg>
);

const BlogIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 4.2c0-.66.54-1.2 1.2-1.2h6.2c.66 0 1.2.54 1.2 1.2v11.6c0 .66-.54 1.2-1.2 1.2H4.2c-.66 0-1.2-.54-1.2-1.2V4.2Z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
    <path
      d="M5.6 6.4h4.6M5.6 9h4.6M5.6 11.6h2.8"
      stroke={active ? "#fff" : "currentColor"}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M13.6 5.6 16.4 6.5c.62.2.96.87.76 1.49l-2.6 8c-.2.63-.87.97-1.49.77l-1.27-.41"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Customer/business shared nav links (replaces the old section quick-jump) ──
const NAV_LINKS = [
  { label: "Home", to: "/home", Icon: HomeIcon },
  { label: "Search", to: "/coupons-list", Icon: SearchIcon },
  { label: "Businesses", to: "/coupons-map", Icon: MapsIcon },
  { label: "Online Brands", to: "/online-brands-list", Icon: BusinessesIcon },
  { label: "Blog", to: "/blogs", Icon: BlogIcon },
];

// ── Sizing below mirrors CustomerHeader.module.scss 1:1 — the same fixed
// sizes up to 1440px, the compact set at 1200–1350px, and the scaled (clamp)
// set from 1441px — so the landing header is identical in size to the app
// header at every width. Tailwind needs each class written out in full, hence
// the repeated variants. ──

// `overHero`: header is transparent over the dark business-page hero photo, so
// inactive links switch to white.
const linkPillClass = (overHero = false) => ({ isActive }: { isActive: boolean }) => `
  flex items-center gap-[6px] h-[36px] px-[clamp(12px,1.6vw,22px)]
  rounded-full whitespace-nowrap no-underline
  font-['Poppins',Helvetica] text-[13px] font-bold
  transition-colors duration-150
  min-[1200px]:max-[1350px]:gap-[4px] min-[1200px]:max-[1350px]:h-[32px] min-[1200px]:max-[1350px]:px-[8px] min-[1200px]:max-[1350px]:text-[11.5px]
  min-[1200px]:max-[1350px]:[&_svg]:w-[14px] min-[1200px]:max-[1350px]:[&_svg]:h-[14px]
  min-[1441px]:gap-[clamp(6px,0.5vw,10px)] min-[1441px]:h-[clamp(36px,2.6vw,52px)]
  min-[1441px]:px-[clamp(22px,1.8vw,34px)] min-[1441px]:text-[clamp(13px,0.65vw+7px,18px)]
  min-[1441px]:[&_svg]:w-[clamp(16px,1vw,22px)] min-[1441px]:[&_svg]:h-[clamp(16px,1vw,22px)]
  ${isActive
    ? "bg-gradient-to-br from-[#ef0023] to-[#d4001d] text-white shadow-[0_3px_10px_rgba(212,0,29,0.35)]"
    : overHero
      ? "text-white hover:bg-white/[0.12]"
      : "text-[#26262b] hover:bg-black/[0.06]"
  }
`;

const mobileLinkPillClass = ({ isActive }: { isActive: boolean }) => `
  flex items-center gap-2 px-3 py-2.5 rounded-[12px] text-left no-underline
  font-['Poppins',Helvetica] text-[13px] font-bold
  transition-colors duration-150
  ${isActive
    ? "bg-gradient-to-br from-[#ef0023] to-[#d4001d] text-white shadow-[0_3px_10px_rgba(212,0,29,0.35)]"
    : "text-[#26262b] bg-black/[0.04] hover:bg-black/[0.08]"
  }
`;

// Pill-button size shared by every desktop button (location / join / sign in).
const pillBtnSize =
  "flex items-center gap-[4px] h-[40px] px-[clamp(12px,1.6vw,16px)] rounded-full no-underline " +
  "font-['Poppins',Helvetica] text-[13px] font-semibold whitespace-nowrap " +
  "min-[1200px]:max-[1350px]:h-[34px] min-[1200px]:max-[1350px]:gap-[3px] min-[1200px]:max-[1350px]:px-[9px] min-[1200px]:max-[1350px]:text-[11.5px] " +
  "min-[1441px]:gap-[clamp(4px,0.4vw,7px)] min-[1441px]:h-[clamp(40px,2.8vw,56px)] " +
  "min-[1441px]:px-[clamp(16px,1.5vw,26px)] min-[1441px]:text-[clamp(13px,0.65vw+7px,18px)] ";

const signinBtnClass =
  pillBtnSize +
  "border border-black/[0.15] bg-transparent text-[#26262b] " +
  "transition-colors duration-150 hover:bg-gradient-to-br hover:from-[#ef0023] hover:to-[#d4001d] " +
  "hover:border-transparent hover:text-white";

// Sign In over the hero photo: white outline + text.
const signinBtnClassOverHero =
  pillBtnSize +
  "border-[1px] border-solid border-white/50 bg-transparent text-white " +
  "transition-colors duration-150 hover:bg-gradient-to-br hover:from-[#ef0023] hover:to-[#d4001d] " +
  "hover:border-transparent hover:text-white";

const primaryBtnClass =
  pillBtnSize +
  "bg-gradient-to-br from-[#ef0023] to-[#d4001d] text-white " +
  "transition-opacity duration-150 hover:opacity-90";

// Stroke-only pill for "Join as a Business" (customer landing page).
const outlineBtnClass =
  pillBtnSize +
  "border-[1px] border-solid border-[#3c3c3c] bg-transparent text-[#3c3c3c] " +
  "transition-colors duration-150 hover:bg-black/5";

// "Join as a Business" over a dark hero photo: white outline + text.
const outlineBtnClassOverHero =
  pillBtnSize +
  "border-[1px] border-solid border-white/50 bg-[transparent] text-[#FFFFFF] " +
  "transition-colors duration-150 hover:bg-white/10";

const outlineBtnClassMobile =
  "flex-1 flex items-center justify-center h-11 px-5 rounded-full border-[1px] border-solid border-[#3c3c3c] bg-transparent no-underline " +
  "font-['Poppins',Helvetica] text-[14px] font-semibold text-[#3c3c3c] " +
  "transition-colors duration-150 hover:bg-black/5";

const secondaryBtnClassMobile =
  "flex-1 flex items-center justify-center h-11 px-5 rounded-full border border-black/[0.15] bg-transparent no-underline " +
  "font-['Poppins',Helvetica] text-[14px] font-semibold text-[#26262b] " +
  "transition-colors duration-150 hover:bg-black/5 hover:border-black/25";

const primaryBtnClassMobile =
  "flex-1 flex items-center justify-center h-11 px-5 rounded-full bg-gradient-to-br from-[#ef0023] to-[#d4001d] no-underline " +
  "font-['Poppins',Helvetica] text-[14px] font-semibold text-white";

export const HeaderWrapper: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isBusinessPage = location.pathname === "/business";
  // Every page: the header is transparent at the top of the page and turns into the
  // normal white blurred header once scrolled or when the mobile menu is open.
  const path = location.pathname.toLowerCase();
  const isLaunchPage = path === "/launch" || path.startsWith("/launch-");
  const hasDarkHero = isBusinessPage || isLaunchPage;
  const transparentHeader = !scrolled && !mobileOpen;
  // `overHero` also flips links/buttons to white — only over the dark hero photos.
  const overHero = hasDarkHero && !scrolled && !mobileOpen;

  // Reset scroll position on route change
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll(); // sync on mount (e.g. reload mid-page) so the transparent state isn't wrong
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-300
          ${transparentHeader
            ? "bg-[transparent]"
            : scrolled
              ? "bg-white/95 backdrop-blur-md shadow-[0_2px_24px_rgba(0,0,0,0.08)]"
              : "bg-white/80 backdrop-blur-sm"
          }
        `}
      >
        <div className="max-w-[1400px] mx-auto px-[clamp(16px,3vw,32px)] py-[10px] flex items-center gap-[16px] min-[1200px]:max-[1350px]:gap-[6px] min-[1200px]:max-[1350px]:py-[8px] min-[1200px]:max-[1350px]:px-[clamp(10px,1.4vw,16px)] min-[1441px]:max-w-[2000px] min-[1441px]:gap-[clamp(16px,1.1vw,26px)] min-[1441px]:py-[clamp(10px,0.8vw,18px)] min-[1441px]:px-[clamp(32px,2.6vw,60px)]">

          {/* ── LOGO ── */}
          <a href="/landing" className="flex items-center shrink-0 min-w-0 max-w-[250px] max-[640px]:max-w-[175px] min-[1200px]:max-[1350px]:max-w-[130px] min-[1441px]:max-w-[clamp(250px,15vw,340px)]">
            <img
              src={MyCouponLogo}
              alt="My Couponbook"
              className="block max-h-[56px] max-w-full w-auto h-auto object-contain max-[640px]:hidden min-[1200px]:max-[1350px]:max-h-[40px] min-[1441px]:max-h-[clamp(56px,3.4vw,78px)]"
            />
            <img
              src={MyCouponLogoMobile}
              alt="My Couponbook"
              className="hidden max-[640px]:block max-h-[40px] max-w-full w-auto h-auto object-contain"
            />
          </a>

          {/* ── SHARED NAV LINKS (Home / Search / Businesses / Maps / Blog) ── */}
          <div className={`hidden min-[1200px]:flex items-center gap-[6px] h-[48px] px-[8px] min-[1200px]:max-[1350px]:gap-0 min-[1200px]:max-[1350px]:px-[3px] min-[1441px]:gap-[clamp(6px,0.5vw,12px)] min-[1441px]:h-[clamp(48px,3.2vw,68px)] min-[1441px]:px-[clamp(8px,0.7vw,14px)] rounded-full ${overHero ? "bg-white/[0.18] backdrop-blur-md" : "bg-black/[0.07]"} flex-1 min-w-0 max-w-fit ml-auto`}>
            {NAV_LINKS.map(({ label, to, Icon }) => (
              <NavLink key={to} to={to} className={linkPillClass(overHero)}>
                {({ isActive }) => (
                  <>
                    <Icon active={isActive} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          {/* Spacer to push CTA right on smaller breakpoints where the row above is hidden */}
          <div className="flex-1 min-[1200px]:hidden" />

          {/* ── CTA ── */}
          {isBusinessPage ? (
            <div className="hidden min-[1200px]:flex items-center gap-[clamp(8px,1.5vw,16px)] min-[1200px]:max-[1350px]:gap-[4px] min-[1441px]:gap-[clamp(16px,1.2vw,24px)] shrink-0">
              <a href="https://mycouponbook.ca/login" className={overHero ? signinBtnClassOverHero : signinBtnClass}>
                Sign In
              </a>
              <a href="https://mycouponbook.ca/signup" className={primaryBtnClass}>
                Sign Up
              </a>
            </div>
          ) : (
            <div className="hidden min-[1200px]:flex items-center gap-[clamp(8px,1.5vw,16px)] min-[1200px]:max-[1350px]:gap-[4px] min-[1441px]:gap-[clamp(16px,1.2vw,24px)] shrink-0">
              <button onClick={() => navigate("/business")} className={overHero ? outlineBtnClassOverHero : outlineBtnClass}>
                Join as a Business
              </button>
            </div>
          )}

          {/* ── MOBILE HAMBURGER ── */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`min-[1200px]:hidden w-[40px] h-[40px] flex flex-col items-center justify-center gap-[5px] rounded-full border-[1px] border-solid transition ${overHero ? "border-white/50 hover:bg-white/10" : "border-black/[0.15] hover:bg-black/5"}`}
            aria-label="Toggle menu"
          >
            <span
              className={`block w-5 h-[2px] ${overHero ? "bg-white" : "bg-neutral-800"} rounded-full transition-all duration-300 ${mobileOpen ? "rotate-45 translate-y-[7px]" : ""}`}
            />
            <span
              className={`block w-5 h-[2px] ${overHero ? "bg-white" : "bg-neutral-800"} rounded-full transition-all duration-300 ${mobileOpen ? "opacity-0" : ""}`}
            />
            <span
              className={`block w-5 h-[2px] ${overHero ? "bg-white" : "bg-neutral-800"} rounded-full transition-all duration-300 ${mobileOpen ? "-rotate-45 -translate-y-[7px]" : ""}`}
            />
          </button>
        </div>

        {/* ── MOBILE MENU ── */}
        <div
          className={`
            min-[1200px]:hidden overflow-hidden transition-all duration-300
            ${mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}
            bg-white border-t border-neutral-100
          `}
        >
          <div className="px-6 py-4 flex flex-col gap-1">

            {/* Shared nav links (Home / Search / Businesses / Maps / Blog) */}
            <p className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-2 mt-1">
              Explore
            </p>
            <div className="grid grid-cols-2 gap-2">
              {NAV_LINKS.map(({ label, to, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  className={mobileLinkPillClass}
                >
                  {({ isActive }) => (
                    <>
                      <Icon active={isActive} />
                      {label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>

            {isBusinessPage ? (
              <div className="mt-4 flex flex-col gap-2">
                <div className="flex gap-2">
                  
                 <a href="https://mycouponbook.ca/login"
                    className={secondaryBtnClassMobile}
                  >
                    Sign In
                  </a>
                  
                  <a  href="https://mycouponbook.ca/signup"
                    className={primaryBtnClassMobile}
                  >
                    Sign Up
                  </a>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate("/business");
                  }}
                  className={outlineBtnClassMobile}
                >
                  Join as a Business
                </button>
              </div>
            )}
          </div>
          
        </div>
        
      </header>
    </>
  );
};